import {
  getCachedContentCards,
  logCardClick,
  logCardDismissal,
  logCardImpressions,
  requestContentCardsRefresh,
} from "@braze/web-sdk";
import {
  IContentCards,
  IContentCardsResult,
} from "@origin-digital/event-dispatcher";

import { IBrazeContentCard } from "../consts/interfaces";
import { readLastRefreshDate, writeLastRefreshDate } from "../utils/storage";

import {
  getFilteredRankedCards,
  translateBrazeCardsToContentCards,
} from "./cardUtils";

export const getContentCards = (
  limit: number | undefined,
  location: string | undefined
): IContentCardsResult => {
  try {
    const brazeContentCards = getCachedContentCards();
    // lastUpdated: if null, it means the content cards are still being fetched for this user.
    if (!brazeContentCards.cards.length || !brazeContentCards.lastUpdated) {
      return { cards: [] };
    }

    // filter + rank cards
    const filteredCards = getFilteredRankedCards(
      brazeContentCards.cards as IBrazeContentCard[],
      location,
      limit
    );

    if (!filteredCards.length) {
      return { cards: [] };
    }
    // map cards to result format
    const contentCards = translateBrazeCardsToContentCards(
      filteredCards as IBrazeContentCard[],
      logCardClick,
      logCardDismissal,
      logCardImpressions
    );

    return { cards: contentCards };
  } catch {
    return { cards: [] };
  }
};

export const refeshContentCards = (): Promise<boolean> =>
  new Promise<boolean>((resolve) => {
    requestContentCardsRefresh(
      () => {
        writeLastRefreshDate();
        resolve(true);
      },
      // on failure
      () => resolve(false)
    );
  });

const STALE_TIME_30_MIN = 1000 * 60 * 60 * 30;
export const isContentCardsStale = (
  ttl: number = STALE_TIME_30_MIN
): boolean => {
  const { lastUpdated } = getCachedContentCards();
  if (lastUpdated == null) return true;

  const lastRefresh = readLastRefreshDate();
  if (lastRefresh == null) return true;

  return lastRefresh.getTime() + ttl < Date.now();
};

export const fetchContentCards = async (
  payload: IContentCards["payload"],
  useCached?: boolean
): Promise<IContentCardsResult> => {
  const { forceRefresh, limit, location } = payload;

  const TTL_10s = 1000 * 10;
  if (
    !useCached &&
    (isContentCardsStale() || (forceRefresh && isContentCardsStale(TTL_10s)))
  ) {
    await refeshContentCards();
  }
  return getContentCards(limit, location);
};

export const backroundRefresh = async () => {
  const TTL_60s = 1000 * 60;

  if (isContentCardsStale(TTL_60s)) {
    refeshContentCards();
  }
};
